import React from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import ko from '../../language/Ko-kr.json';
import en from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeSlideUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Components ─────────────────────────────────────────────────────────── */

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
`;

const MascotImage = styled.img`
  width: 260px;
  height: auto;
  object-fit: contain;
  z-index: 10;
  margin-bottom: 12px;
`;

const Shadow = styled.div`
  width: 180px;
  height: 12px;
  background: radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0) 70%);
  margin-bottom: 40px;
  border-radius: 50%;
`;

const Title = styled.h1`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #222;
  text-align: center;
  line-height: 1.4;
  white-space: pre-line;
  margin-bottom: 24px;
  animation: ${fadeSlideUp} 0.8s ease both;
`;

const Subtitle = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #666;
  text-align: center;
  line-height: 1.6;
  white-space: pre-line;
  margin-bottom: 50px;
  animation: ${fadeSlideUp} 0.8s ease both;
  animation-delay: 0.1s;
`;

const StartBtn = styled.button`
  width: 100%;
  max-width: 320px;
  background: #E07040;
  color: #fff;
  border: none;
  border-radius: 30px;
  padding: 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(16, 96, 112, 0.25);
  transition: transform 0.2s, background 0.2s;
  animation: ${fadeSlideUp} 0.8s ease both;
  animation-delay: 0.2s;
  
  &:hover {
    background: #C85E30;
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const Notice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 24px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 12px;
  color: #777;
  animation-delay: 0.3s;
`;

const VersionText = styled.div`
  position: absolute;
  bottom: 24px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 11px;
  color: #B0A080;
  letter-spacing: 0.5px;
  animation: ${fadeSlideUp} 0.8s ease both;
  animation-delay: 0.4s;
`;

const UnloginStart: React.FC = () => {
    return (
        <>
            <GlobalStyle />
            <ResponsiveShell>
                <Container>
                    <MascotImage src={`${process.env.PUBLIC_URL}/logo_image/dojang.png`} alt="Logo Stamp" />
                    <Shadow />
                    
                    <Title>
                        {t.unloginStart.title.split('\n').map((line: string, i: number, arr: string[]) => (
                            <React.Fragment key={i}>{line}{i < arr.length - 1 && <br/>}</React.Fragment>
                        ))}
                    </Title>
                    
                    <Subtitle>
                        {t.unloginStart.subtitle.split('\n').map((line: string, i: number, arr: string[]) => (
                            <React.Fragment key={i}>{line}{i < arr.length - 1 && <br/>}</React.Fragment>
                        ))}
                    </Subtitle>
                    
                    <StartBtn onClick={() => { window.location.href = '/login'; }}>
                        {t.unloginStart.startBtn}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </StartBtn>

                    <Notice>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {t.unloginStart.notice}
                    </Notice>

                    <VersionText>{t.unloginStart.version}</VersionText>
                </Container>
            </ResponsiveShell>
        </>
    );
};

export default UnloginStart;
